<template>
  <v-card-text>
    <v-data-table
      :items="campaigns"
      :headers="headers"
      :loading="loading"
      :items-per-page="25"
      :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      class="row-pointer"
      mobile-breakpoint="0"
      @click:row="rowClick"
    >
      <template #[`item.type`]="{ item }">
        <v-chip
          small
          outlined
        >
          <v-icon
            small
            left
          >
            {{ typeFor(item.type).icon }}
          </v-icon>
          {{ typeFor(item.type).label }}
        </v-chip>
      </template>
      <template #[`item.targets`]="{ item }">
        <v-chip
          v-for="t in item.targets || []"
          :key="t.id"
          small
          class="mr-1 mb-1"
        >
          {{ t.targetName }}
        </v-chip>
      </template>
      <!-- Which networks a social post actually reaches. Pinterest is on every social campaign by default, so
           the row is worth scanning for the ones that go further; email campaigns have no networks. -->
      <template #[`item.networks`]="{ item }">
        <PlatformIcon
          v-for="p in item.selectedPlatforms || []"
          :key="p"
          :platform="p"
          :size="20"
          class="mr-1"
        />
        <span
          v-if="!(item.selectedPlatforms || []).length"
          class="grey--text"
        >
          —
        </span>
      </template>
      <template #[`item.objective`]="{ item }">
        {{ objectiveFor(item.objective) }}
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip
          small
          :color="statusFor(item.status).color"
          dark
        >
          {{ statusFor(item.status).label }}
        </v-chip>
      </template>
      <template #[`item.nextSend`]="{ item }">
        {{ nextSendFor(item) }}
      </template>
      <!-- What this campaign has cost to generate, all steps and all retries. An em dash rather than $0.00
           for a campaign generated before spend carried a campaign id — it wasn't free, we just can't tie
           its calls to it. The per-step breakdown lives on the campaign screen. -->
      <template #[`item.aiCost`]="{ item }">
        <template v-if="item.aiCalls">
          <div>{{ item.aiCost | currencyAi }}</div>
          <div class="caption grey--text">{{ item.aiCalls }} calls</div>
        </template>
        <span
          v-else
          class="grey--text text--lighten-1"
        >
          —
        </span>
      </template>
      <!-- Draft only: once a campaign is scheduled or has started sending it is history, and deleting it here
           (a row away from a misclick) would take its send record with it. -->
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="isDraft(item)"
          icon
          small
          title="Delete campaign"
          :loading="deletingId === item.id"
          :disabled="!!deletingId"
          @click.stop="remove(item)"
        >
          <v-icon small>
            mdi-delete-outline
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-card-text>
</template>

<script>
import { mapGetters } from 'vuex';
import PlatformIcon from '@/components/common/PlatformIcon';
import {
  campaignStatus,
  matchesAudience,
  matchesNetworkReach,
  matchesStatus,
  objectiveName
} from '@/helpers/marketing';

export default {
  name: 'CampaignList',
  components: { PlatformIcon },
  props: {
    typeFilter: {
      type: Number,
      default: null
    },
    // Audience key from helpers/marketing audienceKey(); null for "all audiences".
    audienceFilter: {
      type: String,
      default: null
    },
    // Campaign status ids to show (see MARKETING.CAMPAIGN_STATUS_IDS); empty means every status.
    statusFilter: {
      type: Array,
      default: () => []
    },
    // See NETWORK_REACH in helpers/marketing; null for "any reach".
    networkFilter: {
      type: Number,
      default: null
    }
  },
  data() {
    return { loading: false, deletingId: null };
  },
  computed: {
    ...mapGetters({ allCampaigns: 'campaigns/list', settings: 'campaigns/settings' }),
    // "All networks" means every ENABLED network (Marketing settings), loaded by the parent screen.
    enabledPlatforms() {
      return this.settings.enabledPlatforms || [];
    },
    campaigns() {
      return this.allCampaigns.filter(
        (c) =>
          (!this.typeFilter || c.type === this.typeFilter) &&
          matchesAudience(c, this.audienceFilter) &&
          matchesStatus(c, this.statusFilter) &&
          matchesNetworkReach(c, this.networkFilter, this.enabledPlatforms)
      );
    },
    headers() {
      return [
        { text: 'Name', value: 'name' },
        { text: 'Type', value: 'type', sortable: false },
        { text: 'Targets', value: 'targets', sortable: false },
        { text: 'Networks', value: 'networks', sortable: false },
        { text: 'Objective', value: 'objective' },
        { text: 'Status', value: 'status' },
        { text: 'Next send', value: 'nextSend', sortable: false },
        { text: 'AI cost', value: 'aiCost', align: 'end', width: 110 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 60 }
      ];
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;
      this.$store
        .dispatch('campaigns/getList')
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },
    statusFor: campaignStatus,
    typeFor(type) {
      return type === this.$enums.MARKETING.CAMPAIGN_TYPE.SOCIAL
        ? { label: 'Social', icon: 'mdi-share-variant-outline' }
        : { label: 'Email', icon: 'mdi-email-outline' };
    },
    objectiveFor: objectiveName,
    nextSendFor(item) {
      const dates = (item.items || [])
        .map((i) => i.scheduledSend)
        .filter((d) => !!d)
        .sort();
      // A hand-made campaign is created without a date, so an empty cell is now a normal state worth naming —
      // blank read as "loading" or "we lost it" rather than "nobody has placed this yet".
      return dates.length ? new Date(dates[0]).toLocaleDateString() : 'Not scheduled';
    },
    rowClick(item) {
      // The list is a working surface — you dip into a campaign and come back — so the detail page
      // opens beside it rather than replacing it.
      const href = this.$router.resolve(`/marketing/campaigns/${item.id}`).href;
      window.open(href, '_blank', 'noopener');
    },
    isDraft(item) {
      return item.status === this.$enums.MARKETING.CAMPAIGN_STATUS_IDS.DRAFT;
    },
    async remove(item) {
      const confirmed = await this.$root.$confirm(
        'Delete Campaign',
        `Delete "${item.name}"? This cancels its planned send. If an audience plan created this campaign, ` +
          'that item goes back to Suggested so it can be built again.',
        { color: 'error' }
      );
      if (!confirmed) return;

      this.deletingId = item.id;
      // The store drops it from the shared list, so the row disappears without a refetch.
      this.$store
        .dispatch('campaigns/remove', item.id)
        .then(() => this.$root.$snackbarSuccess('Campaign deleted'))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.deletingId = null;
        });
    }
  }
};
</script>
